import { ActionIcon, Badge, Box, Checkbox, Group, Menu, Popover, Text, TextInput, Tooltip } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import {
  IconCalendar,
  IconCopy,
  IconCurrencyDollar,
  IconDots,
  IconLock,
  IconPlayerPlay,
  IconScissors,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  toRequest,
  useCreateEntry,
  useDeleteEntry,
  useEntryAction,
  useSplitEntry,
  useUpdateEntry,
  type TimeEntry,
  type TimeEntryRequest,
} from '@/api/timeEntries';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { DurationInput } from '@/components/DurationInput';
import { ProjectPicker } from '@/components/ProjectPicker';
import { TagPicker } from '@/components/TagPicker';
import { TimeInput } from '@/components/TimeInput';
import { useApiError } from '@/hooks/useApiError';
import dayjs from '@/lib/dayjs';
import { formatMoney, inZone, timeFormat } from '@/lib/format';
import classes from './EntryRow.module.css';

interface Props {
  entry: TimeEntry;
  selectable?: boolean;
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  /** Anzahl zusammengefasster Einträge (TT-05) */
  groupCount?: number;
  onToggleGroup?: () => void;
  groupExpanded?: boolean;
  /** Summe der Gruppe statt Einzeldauer */
  groupSeconds?: number;
  nested?: boolean;
}

/** Eine Zeile der Eintragsliste mit Inline-Bearbeitung (TT-06). */
export function EntryRow({ entry, selectable, selected, onSelect, groupCount, onToggleGroup, groupExpanded, groupSeconds, nested }: Props) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { workspaceId, prefs, canSeeAmounts } = ctx;
  const onError = useApiError();
  const update = useUpdateEntry(workspaceId);
  const remove = useDeleteEntry(workspaceId);
  const restore = useCreateEntry(workspaceId);
  const duplicate = useEntryAction(workspaceId, 'duplicate');
  const resume = useEntryAction(workspaceId, 'continue');
  const split = useSplitEntry(workspaceId);
  const [description, setDescription] = useState(entry.description ?? '');
  const [dateOpen, setDateOpen] = useState(false);
  useEffect(() => setDescription(entry.description ?? ''), [entry.description]);

  const start = inZone(entry.start, prefs);
  const end = entry.end ? inZone(entry.end, prefs) : null;
  const locked = entry.locked;
  const isBreak = entry.type === 'BREAK';
  const tf = timeFormat(prefs);

  const save = (changes: Partial<TimeEntryRequest>) =>
    update.mutate({ id: entry.id, ...toRequest(entry, changes) }, { onError });

  const setStartTime = (h: number, m: number) => {
    const s = start.hour(h).minute(m).second(0);
    let e = end ?? s;
    if (!e.isAfter(s)) e = e.add(1, 'day');
    save({ start: s.toISOString(), end: e.toISOString() });
  };
  const setEndTime = (h: number, m: number) => {
    let e = start.hour(h).minute(m).second(0);
    if (!e.isAfter(start)) e = e.add(1, 'day');
    save({ end: e.toISOString() });
  };
  const setDuration = (seconds: number) => save({ end: start.add(seconds, 'second').toISOString() });
  const setDate = (date: string) => {
    const d = dayjs.tz(date, prefs.timezone);
    const s = start.year(d.year()).month(d.month()).date(d.date());
    const duration = end ? end.diff(start, 'second') : 0;
    save({ start: s.toISOString(), end: s.add(duration, 'second').toISOString() });
    setDateOpen(false);
  };

  const deleteWithUndo = () => {
    remove.mutate(entry.id, {
      onSuccess: () => {
        const id = notifications.show({
          message: (
            <Group justify="space-between">
              <Text size="sm">{t('tracker.deleted')}</Text>
              <Text
                size="sm"
                c="orange"
                fw={600}
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  restore.mutate({ id: entry.id, ...toRequest(entry) }, { onError });
                  notifications.hide(id);
                }}
              >
                {t('common.undo')}
              </Text>
            </Group>
          ),
          autoClose: 6000,
        });
      },
      onError,
    });
  };

  const seconds = groupSeconds ?? entry.durationSeconds;

  return (
    <Group className={classes.row} data-nested={nested || undefined} gap="xs" wrap="nowrap" px="sm" py={6}>
      {selectable && (
        <Checkbox
          checked={!!selected}
          onChange={(e) => onSelect?.(e.currentTarget.checked)}
          aria-label={t('tracker.select')}
          disabled={locked}
        />
      )}
      {groupCount && groupCount > 1 ? (
        <Badge
          variant={groupExpanded ? 'filled' : 'light'}
          color="gray"
          circle
          style={{ cursor: 'pointer' }}
          onClick={onToggleGroup}
          aria-label={t('tracker.groupCount', { count: groupCount })}
        >
          {groupCount}
        </Badge>
      ) : null}
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Group gap="xs" wrap="nowrap">
          <TextInput
            variant="unstyled"
            value={description}
            placeholder={isBreak ? t('tracker.break') : t('tracker.addDescription')}
            onChange={(e) => setDescription(e.currentTarget.value)}
            onBlur={() => description !== (entry.description ?? '') && save({ description })}
            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            readOnly={locked}
            size="sm"
            style={{ flex: 1, minWidth: 80 }}
            aria-label={t('tracker.description')}
          />
          {isBreak ? (
            <Text size="sm" c="dimmed">
              {t('tracker.break')}
            </Text>
          ) : (
            <ProjectPicker
              value={{ projectId: entry.projectId, taskId: entry.taskId }}
              disabled={locked}
              onChange={(sel, project) =>
                save({
                  projectId: sel.projectId ?? undefined,
                  taskId: sel.taskId ?? undefined,
                  billable: project ? project.billable : entry.billable,
                })
              }
            />
          )}
        </Group>
      </Box>
      <Group gap={4} wrap="nowrap" visibleFrom="md">
        {entry.overtime && (
          <Badge size="sm" variant="light" color="grape">
            {t('tracker.overtime')}
          </Badge>
        )}
        {entry.invoiced && (
          <Badge size="sm" variant="light" color="teal">
            {t('tracker.invoiced')}
          </Badge>
        )}
        {entry.approvalStatus === 'APPROVED' && (
          <Badge size="sm" variant="light" color="green">
            {t('tracker.approved')}
          </Badge>
        )}
        {entry.approvalStatus === 'PENDING' && (
          <Badge size="sm" variant="light" color="yellow">
            {t('tracker.pending')}
          </Badge>
        )}
        {locked && (
          <Tooltip label={t('tracker.locked')}>
            <IconLock size={14} color="var(--mantine-color-dimmed)" />
          </Tooltip>
        )}
      </Group>
      {!isBreak && (
        <TagPicker value={entry.tagIds} disabled={locked} onChange={(tagIds) => save({ tagIds })} />
      )}
      {!isBreak && (
        <Tooltip label={entry.billable ? t('tracker.billable') : t('tracker.nonBillable')}>
          <ActionIcon
            variant="subtle"
            color={entry.billable ? 'orange' : 'gray'}
            onClick={() => save({ billable: !entry.billable })}
            disabled={locked}
            aria-label={t('tracker.billable')}
            aria-pressed={entry.billable}
          >
            <IconCurrencyDollar size={18} />
          </ActionIcon>
        </Tooltip>
      )}
      {canSeeAmounts && entry.amount != null && entry.amount > 0 && (
        <Text size="xs" c="dimmed" visibleFrom="lg" miw={70} ta="right">
          {formatMoney(entry.amount, entry.currency, prefs.locale)}
        </Text>
      )}
      <Group gap={2} wrap="nowrap" visibleFrom="sm">
        <TimeInput display={start.format(tf)} onCommit={setStartTime} readOnly={locked} w={72} size="sm" variant="unstyled" ta="center" aria-label={t('tracker.startTime')} />
        <Text c="dimmed" size="sm">
          –
        </Text>
        <TimeInput display={end ? end.format(tf) : ''} onCommit={setEndTime} readOnly={locked} w={72} size="sm" variant="unstyled" ta="center" aria-label={t('tracker.endTime')} />
      </Group>
      <Popover opened={dateOpen} onChange={setDateOpen} position="bottom-end" withinPortal>
        <Popover.Target>
          <ActionIcon variant="subtle" color="gray" onClick={() => !locked && setDateOpen((o) => !o)} aria-label={t('tracker.date')} visibleFrom="sm">
            <IconCalendar size={16} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <DatePicker value={start.format('YYYY-MM-DD')} onChange={(v) => v && setDate(dayjs(v).format('YYYY-MM-DD'))} />
        </Popover.Dropdown>
      </Popover>
      <DurationInput
        seconds={seconds}
        onCommit={setDuration}
        readOnly={locked || (groupCount ?? 1) > 1}
        w={84}
        size="sm"
        variant="unstyled"
        fw={600}
        ta="right"
        aria-label={t('tracker.duration')}
      />
      <Tooltip label={t('tracker.continue')}>
        <ActionIcon variant="subtle" color="gray" onClick={() => resume.mutate(entry.id, { onError })} aria-label={t('tracker.continue')}>
          <IconPlayerPlay size={16} />
        </ActionIcon>
      </Tooltip>
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon variant="subtle" color="gray" aria-label={t('common.more')}>
            <IconDots size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item leftSection={<IconCopy size={14} />} onClick={() => duplicate.mutate(entry.id, { onError })}>
            {t('tracker.duplicate')}
          </Menu.Item>
          {end && !locked && (
            <Menu.Item
              leftSection={<IconScissors size={14} />}
              onClick={() =>
                split.mutate(
                  { id: entry.id, at: start.add(Math.round(end.diff(start, 'second') / 2), 'second').toISOString() },
                  { onError },
                )
              }
            >
              {t('tracker.splitHalf')}
            </Menu.Item>
          )}
          {!locked && (
            <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={deleteWithUndo}>
              {t('common.delete')}
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
